import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Badge,
  Input,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, OpenRegular, SaveRegular } from '@fluentui/react-icons';
import { format } from 'date-fns';
import type { PreparerBill } from '../types';
import {
  preparerBillStatusDisplayNames,
  preparerBillStatusColors,
} from '../types';
import { getOrgAwareXeroUrl } from '../utils/xero';
import { updatePreparerBillLineItems } from '../services/api';
import { realLinkProps } from './links/realLinkProps';

interface BillDetailPanelProps {
  bill: PreparerBill | null;
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  summaryItem: {
    padding: '8px 0',
  },
  summaryLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  summaryValue: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
    marginTop: '8px',
  },
  totalLabel: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  totalAmount: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: '#10B981',
  },
  clientLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  sourceTag: {
    display: 'inline-block',
    fontSize: '11px',
    padding: '1px 6px',
    borderRadius: '4px',
    marginLeft: '6px',
    fontWeight: '500',
  },
  sharedTag: {
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
  },
  cashTag: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
  },
  amountInput: {
    width: '100px',
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    gap: '12px',
  },
  changedIndicator: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteYellowForeground1,
    fontStyle: 'italic',
  },
});

export default function BillDetailPanel({ bill, open, onClose }: BillDetailPanelProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Local state for edited amounts keyed by taxReturnId
  const [editedAmounts, setEditedAmounts] = useState<Record<string, string>>({});

  // Reset edited amounts when bill changes
  useEffect(() => {
    setEditedAmounts({});
  }, [bill?.id]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!bill) throw new Error('No bill');
      const lineItems = bill.lineItems.map((item) => ({
        taxReturnId: item.taxReturnId,
        amount: editedAmounts[item.taxReturnId] !== undefined
          ? parseFloat(editedAmounts[item.taxReturnId]) || 0
          : item.amount,
      }));
      return updatePreparerBillLineItems(bill.id, bill.preparerId, lineItems);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preparerBills'] });
      setEditedAmounts({});
      onClose();
    },
  });

  // Compute whether anything has changed
  const hasChanges = useMemo(() => {
    if (!bill) return false;
    return Object.keys(editedAmounts).some((taxReturnId) => {
      const original = bill.lineItems.find((li) => li.taxReturnId === taxReturnId);
      if (!original) return false;
      const edited = parseFloat(editedAmounts[taxReturnId]);
      return !isNaN(edited) && edited !== original.amount;
    });
  }, [bill, editedAmounts]);

  // Compute the live total from original + edits
  const liveTotal = useMemo(() => {
    if (!bill) return 0;
    return bill.lineItems.reduce((sum, item) => {
      const edited = editedAmounts[item.taxReturnId];
      const amt = edited !== undefined ? (parseFloat(edited) || 0) : (item.amount ?? 0);
      return sum + amt;
    }, 0);
  }, [bill, editedAmounts]);

  if (!bill) return null;

  const billDate = bill.generatedDate
    ? format(new Date(bill.generatedDate), 'MMMM d, yyyy')
    : format(new Date(bill.createdAt), 'MMMM d, yyyy');

  const ratePercent = Math.round((bill.commissionRate || 0.30) * 100);
  const isEditable = bill.status !== 'paid';

  const handleAmountChange = (taxReturnId: string, value: string) => {
    setEditedAmounts((prev) => ({ ...prev, [taxReturnId]: value }));
  };

  return (
    <Drawer
      type="overlay"
      position="end"
      size="medium"
      open={open}
      onOpenChange={(_, data) => { if (!data.open) onClose(); }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={onClose}
              aria-label="Close"
            />
          }
        >
          Bill Details
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {/* Summary Section */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Summary</div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Preparer</span>
              <span className={styles.summaryValue}>{bill.preparerName}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Date</span>
              <span className={styles.summaryValue}>{billDate}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Commission Rate</span>
              <span className={styles.summaryValue}>{ratePercent}%</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Status</span>
              <Badge
                appearance="filled"
                style={{ backgroundColor: preparerBillStatusColors[bill.status] }}
              >
                {preparerBillStatusDisplayNames[bill.status] || bill.status}
              </Badge>
            </div>
            {bill.xeroBillId && (
              <>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Xero Bill</span>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<OpenRegular />}
                    as="a"
                    href={getOrgAwareXeroUrl(bill.xeroUrl || '')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    #{bill.xeroBillNumber}
                  </Button>
                </div>
                <div />
              </>
            )}
            {bill.status === 'paid' && bill.paidDate && (
              <>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Paid Date</span>
                  <span className={styles.summaryValue}>
                    {format(new Date(bill.paidDate), 'MMM d, yyyy')}
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Payment Method</span>
                  <span className={styles.summaryValue}>
                    {bill.paymentMethod || '—'}
                    {bill.paymentReference && ` (${bill.paymentReference})`}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Returns Table */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            Included Returns ({bill.lineItems.length})
          </div>
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Client</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Source</TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>Commission</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bill.lineItems.map((item, idx) => {
                const editedValue = editedAmounts[item.taxReturnId];
                const displayAmount = editedValue !== undefined
                  ? editedValue
                  : (item.amount ?? 0).toFixed(2);

                return (
                  <TableRow key={idx}>
                    <TableCell>
                      <a
                        className={styles.clientLink}
                        {...realLinkProps(`/tax-returns/${item.taxReturnId}`, {
                          navigate,
                          onFollow: onClose,
                        })}
                      >
                        {item.clientName}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge appearance="outline" size="small">
                        {item.returnType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.amountSource === 'shared' && (
                        <span className={mergeClasses(styles.sourceTag, styles.sharedTag)}>Shared</span>
                      )}
                      {item.amountSource === 'cash' && (
                        <span className={mergeClasses(styles.sourceTag, styles.cashTag)}>Cash</span>
                      )}
                      {(!item.amountSource || item.amountSource === 'invoice') && (
                        <span style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>Invoice</span>
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {isEditable ? (
                        <Input
                          className={styles.amountInput}
                          size="small"
                          type="number"
                          contentBefore={<span style={{ fontSize: '12px' }}>$</span>}
                          value={displayAmount}
                          onChange={(_, data) => handleAmountChange(item.taxReturnId, data.value)}
                          step="0.01"
                          min="0"
                          appearance="underline"
                        />
                      ) : (
                        <span style={{ fontWeight: 600 }}>
                          ${(item.amount ?? 0).toFixed(2)}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Total */}
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total Commission</span>
            <span className={styles.totalAmount}>${liveTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Save Button */}
        {isEditable && hasChanges && (
          <div className={styles.footerActions}>
            <span className={styles.changedIndicator}>
              Unsaved changes
            </span>
            <Button
              appearance="primary"
              icon={<SaveRegular />}
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}

        {/* Notes */}
        {bill.notes && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Notes</div>
            <p style={{ margin: 0, color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200 }}>
              {bill.notes}
            </p>
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
}
